import { useGetUser } from "@/queries";
import { UserRole, UserStatus } from "@/types";
import { formatDate } from "@/utils";
import { AccessTime } from "@mui/icons-material";
import { CreditCardOff } from "@mui/icons-material";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import { FC } from "react";
import { Loading } from "react-admin";
import { useParams } from "react-router-dom";
import { useReissuePaymentLink } from "../../../vetting-users/queries";

const SubscriptionBlock: FC = () => {
  const { id } = useParams();

  const { user, isUserLoading } = useGetUser(id ?? "");
  const { reissuePaymentLink, isReissueLoading } = useReissuePaymentLink();

  if (isUserLoading) {
    return <Loading />;
  }

  const isSubscriptionExist =
    user?.subscription && !!Object.keys(user.subscription).length;

  const isTrialSubscriptionExist =
    !!user?.hasInfiniteFreeTrial ||
    (user?.freeTrialEndDate
      ? new Date(user.freeTrialEndDate).valueOf() >= new Date().valueOf()
      : false);

  const isApprovedAlphaWithoutSubscription =
    user?.role === UserRole.ALPHA &&
    (user?.status === UserStatus.NOT_FINISHED_PROFILE ||
      user?.status === UserStatus.ACTIVE) &&
    !isSubscriptionExist &&
    !isTrialSubscriptionExist;

  return (
    <Stack width={"100%"} sx={{ borderRight: "1px solid #333" }}>
      <Typography variant="h6" gutterBottom>
        Subscription
      </Typography>

      <Box>
        {isSubscriptionExist || isTrialSubscriptionExist ? (
          <>
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <Chip
                label={
                  isTrialSubscriptionExist
                    ? "Get Started Free"
                    : user.subscription?.plan.isOneTime
                    ? "One-Time"
                    : user.subscription?.plan.tag
                }
                color="secondary"
                variant="filled"
                size="small"
              />
              {!!isSubscriptionExist && (
                <Typography variant="body2" color="gray">
                  {user.subscription?.plan?.title}
                </Typography>
              )}
            </Stack>

            {!!isSubscriptionExist && (
              <>
                <Typography
                  variant="h4"
                  component="div"
                  fontWeight="bold"
                  mb={1}
                >
                  {`$${(user.subscription?.product?.price ?? 0) / 100} / ${
                    user.subscription?.plan.duration.days ||
                    user.subscription?.plan.duration.months
                  } ${
                    Object.keys(user.subscription?.plan.duration || {})[0] ?? ""
                  }`}
                </Typography>

                <Typography variant="body2" mb={1} color="gray">
                  {user.subscription?.plan?.description}
                </Typography>
              </>
            )}
            {isTrialSubscriptionExist && (
              <Typography variant="body2" mb={1} color="gray">
                Free Trial – No Credit Card Required
              </Typography>
            )}

            <Stack direction="row" spacing={1} alignItems="center">
              <AccessTime fontSize="small" sx={{ color: "gray" }} />
              {user.hasInfiniteFreeTrial ? (
                <Typography variant="body2" color="gray">
                  Always
                </Typography>
              ) : (
                <Typography variant="body2" color="gray">
                  Until{" "}
                  {formatDate(
                    (isTrialSubscriptionExist
                      ? user.freeTrialEndDate
                      : user.subscription?.availableTo) ?? ""
                  )}
                </Typography>
              )}
            </Stack>
          </>
        ) : (
          <Stack
            direction={"column"}
            alignItems={"center"}
            rowGap={1}
            width={"100%"}
          >
            <CreditCardOff fontSize={"large"} />
            <Typography>No data</Typography>
            {isApprovedAlphaWithoutSubscription && (
              <Button
                variant="contained"
                color="info"
                size="small"
                loading={isReissueLoading}
                disabled={isReissueLoading}
                onClick={() => reissuePaymentLink({ userId: user.id })}
              >
                Re-issue Payment Link
              </Button>
            )}
          </Stack>
        )}
      </Box>
    </Stack>
  );
};

export { SubscriptionBlock };
